/**
 * @param {unknown} employee
 * @returns {boolean} True if the email is already linked to an employee record
 */
function hasLinkedEmployee(employee) {
  if (typeof employee === "boolean") {
    return employee === true;
  }
  if (employee == null || employee === "") {
    return false;
  }
  if (typeof employee === "string") {
    return employee.trim() !== "";
  }
  if (typeof employee === "object") {
    return true;
  }
  return true;
}

/**
 * Flat check-email API uses booleans for `user` / `access`; legacy responses used ids or strings.
 * @param {unknown} v
 * @returns {boolean}
 */
function isAffirmativeFlag(v) {
  if (typeof v === "boolean") {
    return v === true;
  }
  return v != null && v !== "";
}

/**
 * @param {Record<string, unknown>} response
 * @returns {boolean} User exists on the account and has access, but not linked to an employee
 */
function hasUserWithAccessNotLinkedToEmployee(response) {
  if (!response || typeof response !== "object") {
    return false;
  }
  const { user, access, employee } = response;
  const uSet = isAffirmativeFlag(user);
  const aSet = isAffirmativeFlag(access);
  return uSet && aSet && !hasLinkedEmployee(employee);
}

/**
 * Display / comparison name: prefers employee_name, then used_by, then user_first + user_last.
 * @param {Record<string, unknown>} response
 * @returns {string}
 */
function formatNameFromCheckEmailResponse(response) {
  if (!response || typeof response !== "object") {
    return "";
  }
  if (response.employee_name != null && String(response.employee_name).trim() !== "") {
    return String(response.employee_name).trim();
  }
  if (response.used_by != null) {
    return typeof response.used_by === "string" ? response.used_by : String(response.used_by);
  }
  const a = (response.user_first_name != null && String(response.user_first_name).trim()) || "";
  const b = (response.user_last_name != null && String(response.user_last_name).trim()) || "";
  return [a, b].filter(Boolean).join(" ");
}

/**
 * API may send role as access_level (legacy) or admin_permission.
 * @param {unknown} level
 * @returns {string}
 */
function mapAccessLevelToWizardRole(level) {
  if (level == null || level === "") {
    return "app-User";
  }
  const s = String(level).trim();
  const lower = s.toLowerCase();
  if (lower === "user" || lower === "none") {
    return "app-User";
  }
  if (lower === "co-owner" || lower === "co_owner") {
    return "co-Owner";
  }
  if (lower === "admin") {
    return "admin";
  }
  if (lower === "owner") {
    return "owner";
  }
  return s;
}

/**
 * Normalized name for comparing "account user" vs "current employee" (no spaces, lowercased),
 * matching legacy checkUserEmail behavior.
 * @param {string} first
 * @param {string} last
 */
function normalizeCompactNameParts(first, last) {
  return String((first || "") + (last || ""))
    .toLowerCase()
    .replace(/\s/g, "");
}

/**
 * v2 check-email may put closed/blocked on `user_state` instead of top-level `closed` / `blocked`.
 * @param {Record<string, unknown>} response
 */
function userStateKey(response) {
  return String(response.user_state != null ? response.user_state : "")
    .trim()
    .toLowerCase();
}

/**
 * Maps check-email API payload (legacy or updated flat shape) to the object
 * returned by a GiveAccessToEmployeeWizard email check handler, before
 * {@link normalizeGiveAccessHandlerResult}.
 * @param {Record<string, unknown> | null | undefined} response
 * @returns {Record<string, unknown>}
 */
export function mapCheckEmailApiToGiveAccessHandlerResult(response) {
  if (!response || typeof response !== "object") {
    return { available: true };
  }
  if (response.blocked) {
    return { blocked: true, available: false };
  }
  if (response.closed) {
    return { closed: true, available: false };
  }
  const us = userStateKey(response);
  if (us === "closed") {
    return { closed: true, available: false };
  }
  if (us === "blocked") {
    return { blocked: true, available: false };
  }
  if (hasLinkedEmployee(response.employee)) {
    const name = formatNameFromCheckEmailResponse(response);
    return {
      emailUsed: true,
      existingUser: name,
      available: false,
    };
  }
  if (hasUserWithAccessNotLinkedToEmployee(response)) {
    const level = response.admin_permission ?? response.access_level;
    const detailsRole = mapAccessLevelToWizardRole(level);
    return {
      detailsRole,
      available: false,
    };
  }
  return { available: true };
}

/**
 * For GiveAccessToEmployeeModal / legacy modals: maps the same API to modal state
 * (dataType, existingUser, access_level).
 * @param {Record<string, unknown> | null | undefined} response
 * @param {{ getFirst: () => string, getLast: () => string }} currentEmployee
 */
export function mapCheckEmailApiToLegacyModalState(
  response,
  { getFirst = () => "", getLast = () => "" } = {}
) {
  if (!response || typeof response !== "object") {
    return { dataType: "step2" };
  }
  if (response.blocked) {
    return { dataType: "emailBlocked" };
  }
  if (response.closed) {
    return { dataType: "emailClosed" };
  }
  const legacyUs = userStateKey(response);
  if (legacyUs === "closed") {
    return { dataType: "emailClosed" };
  }
  if (legacyUs === "blocked") {
    return { dataType: "emailBlocked" };
  }
  if (hasLinkedEmployee(response.employee)) {
    return {
      dataType: "emailUsed",
      existingUser:
        response.used_by != null
          ? response.used_by
          : formatNameFromCheckEmailResponse(response),
    };
  }
  if (hasUserWithAccessNotLinkedToEmployee(response)) {
    const first = getFirst() || "";
    const last = getLast() || "";
    const userDisplay = formatNameFromCheckEmailResponse(response);
    const employeeNameNorm = normalizeCompactNameParts(first, last);
    const usedNorm = normalizeCompactNameParts(userDisplay, "");
    if (userDisplay && usedNorm === employeeNameNorm) {
      return {
        dataType: "details_email_user",
        existingUser: userDisplay,
        access_level: mapAccessLevelToWizardRole(
          response.admin_permission ?? response.access_level
        ),
      };
    }
    return {
      dataType: "accessedEmail",
      existingUser: userDisplay,
      access_level: mapAccessLevelToWizardRole(
        response.admin_permission ?? response.access_level
      ),
    };
  }
  return { dataType: "step2" };
}

/**
 * Give-access email checks: preset mocks, optional handler, and normalization.
 *
 * Preset mock addresses (opt-in via usePresetMockEmails on GiveAccessToEmployeeWizard):
 * - closed@giveaccess.mock.local → email closed (system)
 * - blocked@giveaccess.mock.local → email blocked (system)
 * - used@giveaccess.mock.local → already linked to another employee
 * - owner@giveaccess.mock.local → account owner email, not linked to this employee
 * - coowner@giveaccess.mock.local → co-owner access, not linked to this employee
 * - admin@giveaccess.mock.local → admin access, not linked to this employee
 *
 * Any other address → proceed to access-type flow (same as an available email).
 */

export const GIVE_ACCESS_MOCK_EMAILS = {
  "closed@giveaccess.mock.local": {
    closed: true,
    blocked: false,
    emailUsed: false,
    existingUser: null,
    detailsRole: null,
  },
  "blocked@giveaccess.mock.local": {
    closed: false,
    blocked: true,
    emailUsed: false,
    existingUser: null,
    detailsRole: null,
  },
  "used@giveaccess.mock.local": {
    closed: false,
    blocked: false,
    emailUsed: true,
    existingUser: "Jordan Lee",
    detailsRole: null,
  },
  "owner@giveaccess.mock.local": {
    closed: false,
    blocked: false,
    emailUsed: false,
    existingUser: null,
    detailsRole: "owner",
  },
  "coowner@giveaccess.mock.local": {
    closed: false,
    blocked: false,
    emailUsed: false,
    existingUser: null,
    detailsRole: "co-Owner",
  },
  "admin@giveaccess.mock.local": {
    closed: false,
    blocked: false,
    emailUsed: false,
    existingUser: null,
    detailsRole: "admin",
  },
};

export function emptyGiveAccessEmailState() {
  return {
    closed: false,
    blocked: false,
    emailUsed: false,
    existingUser: null,
    detailsRole: null,
  };
}

/**
 * Preset mocks when the address matches {@link GIVE_ACCESS_MOCK_EMAILS};
 * otherwise treat as available (proceed to next wizard step).
 */
export function getPresetGiveAccessMockState(emailLower) {
  if (!emailLower || typeof emailLower !== "string") {
    return { ...emptyGiveAccessEmailState(), available: true };
  }
  const key = emailLower.trim().toLowerCase();
  const preset = GIVE_ACCESS_MOCK_EMAILS[key];
  if (!preset) {
    return { ...emptyGiveAccessEmailState(), available: true };
  }
  return {
    ...emptyGiveAccessEmailState(),
    ...preset,
    available: false,
  };
}

/**
 * Normalize handler results to the same shape as presets / future API mapping.
 */
export function normalizeGiveAccessHandlerResult(result) {
  if (!result || typeof result !== "object") {
    return { ...emptyGiveAccessEmailState(), available: true };
  }
  const hasStructured =
    "emailUsed" in result ||
    "detailsRole" in result ||
    "closed" in result ||
    "blocked" in result;
  if (hasStructured) {
    const s = {
      ...emptyGiveAccessEmailState(),
      closed: !!result.closed,
      blocked: !!result.blocked,
      emailUsed: !!result.emailUsed,
      existingUser:
        result.existingUser != null ? String(result.existingUser) : null,
      detailsRole: result.detailsRole != null ? String(result.detailsRole) : null,
    };
    if (result.available === false) {
      s.available = false;
    } else if (result.available === true) {
      s.available = true;
    } else {
      s.available = !s.closed && !s.blocked && !s.emailUsed && !s.detailsRole;
    }
    return s;
  }
  if (result.inUse || result.emailUsed) {
    return {
      ...emptyGiveAccessEmailState(),
      emailUsed: true,
      existingUser:
        result.usedBy != null
          ? String(result.usedBy)
          : result.existingUser != null
            ? String(result.existingUser)
            : null,
      available: false,
    };
  }
  if (result.blocked) {
    return { ...emptyGiveAccessEmailState(), blocked: true, available: false };
  }
  if (result.closed) {
    return { ...emptyGiveAccessEmailState(), closed: true, available: false };
  }
  if (result.ok && result.detailsRole) {
    return {
      ...emptyGiveAccessEmailState(),
      detailsRole: String(result.detailsRole),
      available: false,
    };
  }
  if (result.ok) {
    return { ...emptyGiveAccessEmailState(), available: true };
  }
  return { ...emptyGiveAccessEmailState(), available: true };
}

/**
 * Interpret normalized state → modal content types used by the wizard.
 */
export function interpretGiveAccessEmailState(state) {
  if (!state) {
    return { kind: "ok" };
  }
  if (state.closed) {
    return { kind: "emailClosed" };
  }
  if (state.blocked) {
    return { kind: "emailBlocked" };
  }
  if (state.emailUsed) {
    return {
      kind: "emailUsed",
      existingUser: state.existingUser || "",
    };
  }
  if (state.detailsRole) {
    return {
      kind: "details_email",
      access_level: state.detailsRole,
    };
  }
  return { kind: "ok" };
}
